import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from "react-router-dom";
import { useQuery } from 'react-query';
import dashboardApi from '../../api/dashboardApi';
import distributorApi from '../../api/distributorApi';
import { uiChartNames, uiTabNames } from '../../config/constants';
import { linkNavigationBar, categoriesProductsNavigationBar, newLostLocationsNavigationBar } from '../../reducers/UIReducer/UIActions';
import { setDashboardHighlights, setDashboardNewLocations, setDashboardLostLocations, setDashboardTopCategories, setDashboardTopProducts, setTableDataLoading, setChartDataLoading, setDistributorBreakdownChartData, setCompetitiveInsightsChartData, setBepDistributorListLoading, setBEPDistributorListData, setHighlightsLoading } from '../../reducers/DashboardReducer/DashboardActions';
import LocalStorage from '../../sharedComponents/LocalStorage/LocalStorage';
import { setOppsFilters, setOpportunitiesData, setOpportunitiesTableData } from '../../reducers/OpportunitiesReducer/OpportunitiesActions';
import manufacturerApi from '../../api/manufacturerApi';
import { toast } from 'react-toastify';
import moment from 'moment';
import useManufacturerData from "../../sharedComponents/useManufacturerData/useManufacturerData";

const useDashboard = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const setDashboardTab = () => {
    dispatch(linkNavigationBar.setTab(uiTabNames.dashboard));
  }

  const handleClickTopCategoriesTab = () => {
    dispatch(categoriesProductsNavigationBar.setTab(uiTabNames.topCategories));
  }

  const handleClickTopProductsTab = () => {
    dispatch(categoriesProductsNavigationBar.setTab(uiTabNames.topProducts));
  }

  const handleClickNewLocationsTab = () => {
    dispatch(newLostLocationsNavigationBar.setTab(uiTabNames.newLocations));
  }

  const handleClickLostLocationsTab = () => {
    dispatch(newLostLocationsNavigationBar.setTab(uiTabNames.lostLocations));
  }

  const handleClickViewCampaignManagement = () => {
    navigate('/campaignmanagement');
  }

  const filterCsvTable = () => {
    // Declare variables
    var input, filter, table, tr, td, i, txtValue;
    input = document.getElementById("csvSearchInput");
    filter = input.value.toUpperCase();
    table = document.getElementById("distributorCsvTable");
    tr = table.getElementsByTagName("tr");

    // Loop through all table rows, and hide those who don't match the search query
    for (i = 0; i < tr.length; i++) {
      td = tr[i].getElementsByTagName("td")[0];
      if (td) {
        txtValue = td.textContent || td.innerText;
        if (txtValue.toUpperCase().indexOf(filter) > -1) {
          tr[i].style.display = "";
        } else {
          tr[i].style.display = "none";
        }
      }
    }
  }

  return {
    setDashboardTab,
    handleClickTopCategoriesTab,
    handleClickTopProductsTab,
    handleClickNewLocationsTab,
    handleClickLostLocationsTab,
    handleClickViewCampaignManagement,
    filterCsvTable
  }
}

const useDates = () => {
  const dispatch = useDispatch();
  const chainData = useSelector(({user}) => user.chainData);
  const emergingData = useSelector(({user}) => user.emergingData);
  const fromDate = useSelector(({dates}) => dates.dashFromDate);
  const toDate = useSelector(({dates}) => dates.dashToDate);
  const { manufacturerParams, dataManagersParams } = useManufacturerData();
  const dateRanges = {
    'Last 30 Days': [moment().subtract(29, 'days'), moment()],
    'Last 2 Months': [moment().subtract(2, 'month'), moment()],
    'Last Year': [moment().subtract(1, 'year'), moment()],
    'Last 18 Months': [moment().subtract(18, 'month'), moment()]
  };

  const useGetCategoryFilters = () => useQuery({
    queryKey: ['categoryFilters'],
    queryFn: () => dashboardApi.topCategories.post({
      "chainData": chainData,
      "emergingData": emergingData,
      "startDate": fromDate,
      "endDate": toDate,
      ...manufacturerParams
    }).then((response) => {
      return response.data.map(c => c.category);
    }).catch(err => {
      console.log(err);
    }),
    enabled: chainData || emergingData
  });
  const { data: categoryFilters, refetch: refetchCategoryFilters } = useGetCategoryFilters();

  const getHighlights = (selectedFilters = []) => {
    const params = {
      'startDate': selectedFilters?.startDate ?? fromDate,
      'endDate': selectedFilters?.endDate ?? toDate,
      'states': selectedFilters?.states ?? [],
      'countries': selectedFilters?.countries ?? [],
      ...manufacturerParams
    }
    dispatch(setHighlightsLoading(true));
    dashboardApi.highlights.post(params).then(({data}) => {
      dispatch(setHighlightsLoading(false));
      dispatch(setDashboardHighlights(data));
    }).catch(err => {
      console.log(err);
    });
  }

  const getNewLocations = (selectedFilters = []) => {
    const params = {
      'startDate': selectedFilters?.startDate ?? fromDate,
      'endDate': selectedFilters?.endDate ?? toDate,
      'states': selectedFilters?.states ?? [],
      'countries': selectedFilters?.countries ?? [],
      'categories': selectedFilters?.categories ?? [],
      'segments': selectedFilters?.segments ?? [],
      'locations': selectedFilters?.locations ?? [],
      'children': selectedFilters?.children ?? [],
      'distributors': selectedFilters?.distributors ?? [],
      'parentSizeMin': selectedFilters?.parentSizeMin ?? null,
      'parentSizeMax': selectedFilters?.parentSizeMax ?? null,
      ...manufacturerParams
    }
    dispatch(setTableDataLoading(uiTabNames.newLocations, true));
    dashboardApi.newLocations.post(params).then(data => {
      dispatch(setDashboardNewLocations(data.data));
      dispatch(setTableDataLoading(uiTabNames.newLocations, false));
    }).catch(err => {
      console.log(err);
    });
  }

  const getLostLocations = (selectedFilters = []) => {
    const params = {
      'startDate': selectedFilters?.startDate ?? fromDate,
      'endDate': selectedFilters?.endDate ?? toDate,
      'states': selectedFilters?.states ?? [],
      'countries': selectedFilters?.countries ?? [],
      'categories': selectedFilters?.categories ?? [],
      'segments': selectedFilters?.segments ?? [],
      'locations': selectedFilters?.locations ?? [],
      'children': selectedFilters?.children ?? [],
      'distributors': selectedFilters?.distributors ?? [],
      'parentSizeMin': selectedFilters?.parentSizeMin ?? null,
      'parentSizeMax': selectedFilters?.parentSizeMax ?? null,
      ...manufacturerParams
    }
    dispatch(setTableDataLoading(uiTabNames.lostLocations, true));
    dashboardApi.lostLocations.post(params).then(data => {
      dispatch(setDashboardLostLocations(data.data));
      dispatch(setTableDataLoading(uiTabNames.lostLocations, false));
    }).catch(err => {
      console.log(err);
    });
  }

  const getTopCategories = (selectedFilters = []) => {
    const params = {
      'startDate': selectedFilters?.startDate ?? fromDate,
      'endDate': selectedFilters?.endDate ?? toDate,
      'states': selectedFilters?.states ?? [],
      'countries': selectedFilters?.countries ?? [],
      'categories': selectedFilters?.categories ?? [],
      'segments': selectedFilters?.segments ?? [],
      'locations': selectedFilters?.locations ?? [],
      'children': selectedFilters?.children ?? [],
      'distributors': selectedFilters?.distributors ?? [],
      'parentSizeMin': selectedFilters?.parentSizeMin ?? null,
      'parentSizeMax': selectedFilters?.parentSizeMax ?? null,
      ...manufacturerParams
    }
    dispatch(setTableDataLoading(uiTabNames.topCategories, true));
    dashboardApi.topCategories.post(params).then(data => {
      dispatch(setDashboardTopCategories(data.data));
      dispatch(setTableDataLoading(uiTabNames.topCategories, false));
    }).catch(err => {
      console.log(err);
    });
  }

  const getTopProducts = (selectedFilters = []) => {
    const params = {
      'startDate': selectedFilters?.startDate ?? fromDate,
      'endDate': selectedFilters?.endDate ?? toDate,
      'states': selectedFilters?.states ?? [],
      'countries': selectedFilters?.countries ?? [],
      'categories': selectedFilters?.categories ?? [],
      'segments': selectedFilters?.segments ?? [],
      'locations': selectedFilters?.locations ?? [],
      'children': selectedFilters?.children ?? [],
      'distributors': selectedFilters?.distributors ?? [],
      'parentSizeMin': selectedFilters?.parentSizeMin ?? null,
      'parentSizeMax': selectedFilters?.parentSizeMax ?? null,
      ...manufacturerParams
    }
    dispatch(setTableDataLoading(uiTabNames.topProducts, true));
    dashboardApi.topProducts.post(params).then(data => {
      dispatch(setDashboardTopProducts(data.data));
      dispatch(setTableDataLoading(uiTabNames.topProducts, false));
    }).catch(err => {
      console.log(err);
    });
  }

  const getSegmentOverview = (selectedFilters = []) => {
    const params = {
      manufacturerPlatformId: LocalStorage.manufacturerData.get().platform_id,
      startDate: selectedFilters?.startDate ?? fromDate,
      endDate: selectedFilters?.endDate ?? toDate,
      states: selectedFilters?.states ?? [],
      countries: selectedFilters?.countries ?? [],
      categories: selectedFilters?.categories ?? [],
      segments: selectedFilters?.segments ?? [],
      locations: selectedFilters?.locations ?? [],
      children: selectedFilters?.children ?? [],
      distributors: selectedFilters?.distributors ?? [],
      parentSizeMin: selectedFilters?.parentSizeMin ?? null,
      parentSizeMax: selectedFilters?.parentSizeMax ?? null,
      ...manufacturerParams
    };

    dispatch({ type: 'SET_SEGMENT_OVERVIEW_LOADING', payload: { isLoading: true } });

    dashboardApi.segmentOverview.post(params)
      .then(({ data }) => {
        dispatch({ type: 'SET_DASHBOARD_SEGMENT_OVERVIEW', payload: { data }});
        dispatch({ type: 'SET_SEGMENT_OVERVIEW_LOADING', payload: { isLoading: false } });
      })
      .catch(err => console.log(err));
  };

  const getTotalOpportunitiesChartData = (selectedFilters = []) => {
    dispatch(setChartDataLoading(uiChartNames.totalOpportunitiesTable, true));
    var oppTypeFilters = "";
    var oppStatusFilters = "";
    if(selectedFilters?.length !== 0) {
        if(selectedFilters?.oppSourceStrings) {
          oppTypeFilters = selectedFilters.oppSourceStrings;
        }
        if(selectedFilters?.status) {
          oppStatusFilters = selectedFilters.status;
        }
    } else {
      dispatch(setOppsFilters([]));
    }
    const params = {
        'startDate': selectedFilters?.startDate ?? fromDate,
        'endDate': selectedFilters?.endDate ?? toDate,
        'complianceActivity': oppTypeFilters,
        "statuses": oppStatusFilters,
        ...manufacturerParams,
        ...dataManagersParams
    };
    manufacturerApi.agreements.post(params).then(({data}) => {
        dispatch(setOpportunitiesData(data.data.agreements));
        dispatch(setOpportunitiesTableData(data.data.tableData));
        dispatch(setChartDataLoading(uiChartNames.totalOpportunitiesTable, false));
    }).catch(err => {
        console.log(err);
        toast.error("Something went wrong loading your agreement data.", {autoClose: "5000"});
        dispatch(setChartDataLoading(uiChartNames.totalOpportunitiesTable, false));
    });
  }

  const getDistributorBreakdownChartData = (selectedFilters = []) => {
    dispatch(setChartDataLoading(uiChartNames.distributorBreakdownTable, true));
    const params = {
        'startDate': selectedFilters?.startDate ?? fromDate,
        'endDate': selectedFilters?.endDate ?? toDate,
        'states': selectedFilters?.states ?? [],
        'countries': selectedFilters?.countries ?? [],
        'categories': selectedFilters?.categories ?? [],
        'segments': selectedFilters?.segments ?? [],
        'locations': selectedFilters?.locations ?? [],
        'children': selectedFilters?.children ?? [],
        'distributors': selectedFilters?.distributors ?? [],
        'parentSizeMin': selectedFilters?.parentSizeMin ?? null,
        'parentSizeMax': selectedFilters?.parentSizeMax ?? null,
        ...manufacturerParams
    };
    dashboardApi.charts.distributorBreakdown.post(params).then(({data}) => {
        dispatch(setDistributorBreakdownChartData(data));
        dispatch(setChartDataLoading(uiChartNames.distributorBreakdownTable, false));
    }).catch(err => {
        console.log(err);
        toast.error("Something went wrong loading your distributor data.", {autoClose: "5000"});
        dispatch(setChartDataLoading(uiChartNames.distributorBreakdownTable, false));
    });
  }

  const getCompetitiveInsightsChartData = async (selectedFilters = []) => {
    dispatch(setChartDataLoading(uiChartNames.competitiveInsightsTable, true));
    let topCategories = categoryFilters ?? [];
    if (!selectedFilters?.categories || selectedFilters?.categories?.length <= 0) {
      topCategories = (await refetchCategoryFilters()).data ?? [];
    }
    if (topCategories.length > 0 || selectedFilters?.categories?.length > 0) {
      const params = {
        'startDate': selectedFilters?.startDate ?? fromDate,
        'endDate': selectedFilters?.endDate ?? toDate,
        'categories': (selectedFilters?.categories?.length > 0) ? selectedFilters.categories : topCategories,
        'countries': selectedFilters?.countries ?? [],
        'states': selectedFilters?.states ?? [],
        'segments': selectedFilters?.segments ?? [],
        'locations': selectedFilters?.locations ?? [],
        'children': selectedFilters?.children ?? [],
        'distributors': selectedFilters?.distributors ?? [],
        'parentSizeMin': selectedFilters?.parentSizeMin ?? null,
        'parentSizeMax': selectedFilters?.parentSizeMax ?? null,
        ...manufacturerParams
      };
      dashboardApi.charts.competitiveInsights.post(params).then(({data}) => {
        dispatch(setChartDataLoading(uiChartNames.competitiveInsightsTable, false));
        dispatch(setCompetitiveInsightsChartData(data));
      }).catch(err => {
        console.log(err);
        dispatch(setChartDataLoading(uiChartNames.competitiveInsightsTable, false));
      });
    } else {
      dispatch(setChartDataLoading(uiChartNames.competitiveInsightsTable, false));
    }
  }

  const getBEPDistributorListData = () => {
    dispatch(setBepDistributorListLoading(true));
    distributorApi.get().then(({data}) => {
      dispatch(setBepDistributorListLoading(false));
      dispatch(setBEPDistributorListData(data));
    }).catch(err => {
      console.log(err);
      dispatch(setBepDistributorListLoading(false));
    });
  }

  const onClickDownloadCsv = (id) => {
    distributorApi.downloadCsv.get(id).then(({data}) => {
      const csv = data?.csvString;

      const removeHashtags = csv?.replace(/#/g, '');

      const dateSplit = data?.date.split('-');
      const formattedDate = `${dateSplit[0].padStart(2, '0')}_${dateSplit[1]}`;

      var hiddenElement = document.createElement('a');
      hiddenElement.href = 'data:text/csv;charset=utf-8,' + encodeURI(removeHashtags);
      hiddenElement.target = '_blank';

      //provide the name for the CSV file to be downloaded
      hiddenElement.download = `BEP_Distributor_List_${formattedDate}.csv`;
      hiddenElement.click();
    });
  }


  const onChangeHighlightsFilter = (selectedFilters = []) => {
    let beplBusiness = true;
    let directBusiness = false;
    const params = {
      'startDate': selectedFilters?.startDate ?? fromDate,
      'endDate': selectedFilters?.endDate ?? toDate,
      'showBeplBusiness': beplBusiness,
      'showDirectBusiness': directBusiness,
      'countries': selectedFilters?.countries ?? [],
      'states': selectedFilters?.states ?? [],
      ...manufacturerParams
    }
    dispatch(setHighlightsLoading(true));
    dashboardApi.highlights.post(params).then(({data}) => {
      dispatch(setHighlightsLoading(false));
      dispatch(setDashboardHighlights(data));
    }).catch(err => {
      console.log(err);
      dispatch(setHighlightsLoading(false));
    });
  }

  const onApplySearch = (selectedFilters = []) => {
    getHighlights(selectedFilters);
    getNewLocations(selectedFilters);
    getLostLocations(selectedFilters);
    getTopCategories(selectedFilters);
    getTopProducts(selectedFilters);
    getDistributorBreakdownChartData(selectedFilters);
    getCompetitiveInsightsChartData(selectedFilters);
    getSegmentOverview(selectedFilters);
    getTotalOpportunitiesChartData(selectedFilters);
  }

  return {
    toDate,
    fromDate,
    dateRanges,
    onApplySearch,
    getHighlights,
    getNewLocations,
    getLostLocations,
    getTopCategories,
    getTopProducts,
    getTotalOpportunitiesChartData,
    getDistributorBreakdownChartData,
    getCompetitiveInsightsChartData,
    getBEPDistributorListData,
    onClickDownloadCsv,
    onChangeHighlightsFilter,
    getSegmentOverview 
  };
}

// eslint-disable-next-line
export default { useDates, useDashboard };
